
import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '@/hooks/useAuth';
import { supabase } from '@/integrations/supabase/client';
import { debugLogger } from '@/utils/debugLogger';

export const useProfileEditData = () => {
  const { user, profileData, updateProfileData, refreshProfileData } = useAuth();
  const [localData, setLocalData] = useState<any>(null);
  const [loading, setLoading] = useState(true);

  // Fetch fresh data from database on mount
  const fetchFreshData = useCallback(async () => {
    if (!user?.id) {
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      debugLogger.debug('ProfileEditData', 'Fetching fresh data from database');

      const { data, error } = await supabase
        .from('profiles')
        .select('*')
        .eq('id', user.id)
        .single();

      if (error) {
        debugLogger.error('ProfileEditData', 'Error fetching profile:', error);
        // Fallback to context data
        if (profileData) {
          setLocalData({
            ...profileData,
            bio_topics: (profileData as any).bio_topics || [],
            bio_score: (profileData as any).bio_score ?? 0,
            bio_last_analyzed: (profileData as any).bio_last_analyzed
          });
        }
      } else if (data) {
        debugLogger.debug('ProfileEditData', 'Fresh data loaded', { 
          height: data.height,
          occupation: data.occupation,
          body_type: data.body_type
        });
        
        setLocalData({
          ...data,
          bio_topics: data.bio_topics || [],
          bio_score: data.bio_score ?? 0,
          bio_last_analyzed: data.bio_last_analyzed
        });

        // Also update auth context to keep it in sync
        refreshProfileData();
      }
    } catch (error) {
      debugLogger.error('ProfileEditData', 'Unexpected error:', error);
      // Fallback to context data
      if (profileData) {
        setLocalData({
          ...profileData,
          bio_topics: (profileData as any).bio_topics || [],
          bio_score: (profileData as any).bio_score ?? 0,
          bio_last_analyzed: (profileData as any).bio_last_analyzed
        });
      }
    } finally {
      setLoading(false);
    }
  }, [user?.id, profileData, refreshProfileData]);

  // Fetch fresh data on mount
  useEffect(() => {
    fetchFreshData();
  }, [user?.id]); // Only re-fetch when user changes, not on every render

  // Update local data
  const updateLocalData = (field: string, value: any) => {
    debugLogger.debug('ProfileEditData', 'Updating local field:', { field, value });
    setLocalData((prev: any) => prev ? { ...prev, [field]: value } : null);
  };

  // Sync local data with auth context after successful save
  const syncWithAuthContext = async (updatedFields: Record<string, any>) => {
    try {
      await updateProfileData(updatedFields);
      debugLogger.debug('ProfileEditData', 'Successfully synced with auth context');
    } catch (error) {
      debugLogger.error('ProfileEditData', 'Error syncing with auth context:', error);
    }
  };

  return {
    localData,
    updateLocalData,
    syncWithAuthContext,
    loading,
    user,
    profileData,
    refetch: fetchFreshData
  };
};
